import { History } from 'lucide-react'

export interface StateTransition {
  from: string | null
  to: string
  at: string
  actorId: string
  actorEmail: string
}

export interface StateHistoryPayload {
  createdAt: string
  createdBy: string
  currentState: string
  transitions: StateTransition[]
}

function timestamp(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function elapsed(from: string, to: string): string | null {
  const ms = new Date(to).getTime() - new Date(from).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const minutes = Math.round(ms / 60_000)
  if (minutes < 1) return '<1 min later'
  if (minutes < 60) return `${minutes} min later`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} hr${hours === 1 ? '' : 's'} later`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} later`
}

export function StateHistory({
  history,
  stateLabel,
  noun = 'stage',
}: {
  history: StateHistoryPayload
  stateLabel: (state: string) => string
  noun?: 'stage' | 'state'
}) {
  return (
    <section className="rounded-lg border border-border bg-surface-sunken/50 p-3.5">
      <div className="mb-2.5 flex items-center gap-2">
        <History className="size-4 text-text-muted" aria-hidden />
        <p className="microlabel">History · current {noun}: {stateLabel(history.currentState)}</p>
      </div>
      <ol className="grid gap-2 border-l border-border pl-3">
        <li>
          <p className="text-xs font-medium text-foreground">Created</p>
          <p className="text-tiny text-text-muted">
            {timestamp(history.createdAt)}{history.createdBy ? ` · ${history.createdBy}` : ''}
          </p>
        </li>
        {history.transitions.map((transition, index) => {
          const previousAt = index === 0
            ? history.createdAt
            : history.transitions[index - 1]?.at ?? history.createdAt
          const sincePrevious = elapsed(previousAt, transition.at)
          return (
            <li key={`${transition.at}-${transition.to}-${index}`}>
              <p className="text-xs font-medium text-foreground">
                {transition.from ? `${stateLabel(transition.from)} → ` : ''}{stateLabel(transition.to)}
              </p>
              <p className="text-tiny text-text-muted">
                {timestamp(transition.at)}
                {sincePrevious ? ` · ${sincePrevious}` : ''}
                {transition.actorEmail ? ` · ${transition.actorEmail}` : ''}
              </p>
            </li>
          )
        })}
        {history.transitions.length === 0 ? (
          <li className="text-tiny text-text-muted">No recorded {noun} changes yet.</li>
        ) : null}
      </ol>
    </section>
  )
}
